import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { catalog } from '@/mocks/catalog';

const NAV_ITEMS = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
  { label: '资讯', to: '/news' },
  { label: '关于', to: '/about' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return catalog
      .filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          item.genres.some((g) => g.toLowerCase().includes(q)) ||
          item.tags.some((t) => t.toLowerCase().includes(q)),
      )
      .slice(0, 6);
  }, [query]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const submitSearch = (value: string) => {
    const q = value.trim();
    if (!q) return;
    setFocused(false);
    setMobileOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
      isActive
        ? 'text-primary-600 font-semibold bg-primary-50'
        : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
    }`;

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="h-16 md:h-[72px] flex items-center gap-4 md:gap-6">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="快活影院首页">
            <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
              <i className="ri-film-line text-xl"></i>
            </span>
            <span className="font-heading font-extrabold text-lg md:text-xl tracking-tight text-foreground-950">
              快活影院
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === '/'} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Search */}
          <div ref={boxRef} className="hidden md:block relative ml-auto w-full max-w-[280px]">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitSearch(query);
              }}
            >
              <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-background-300 bg-background-100 focus-within:border-primary-400 focus-within:bg-background-50 transition-colors">
                <i className="ri-search-line text-foreground-500 text-sm"></i>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setFocused(true)}
                  placeholder="搜索影视名称、类型"
                  aria-label="搜索影视"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                />
              </div>
            </form>
            {focused && suggestions.length > 0 && (
              <div className="absolute top-[calc(100%+8px)] left-0 w-full rounded-lg border border-background-200 bg-background-50 overflow-hidden shadow-sm">
                <p className="px-3 pt-3 pb-1 text-xs text-foreground-500">推荐结果</p>
                <ul>
                  {suggestions.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => submitSearch(item.title)}
                        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-background-100 transition-colors"
                      >
                        <span className="w-8 h-11 rounded bg-background-200 overflow-hidden shrink-0">
                          <img src={item.poster} alt={item.title} className="w-full h-full object-cover object-top" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm text-foreground-900 truncate">{item.title}</span>
                          <span className="block text-xs text-foreground-500 truncate">
                            {item.year} · {item.genres.slice(0, 2).join(' / ')}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="打开菜单"
            className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-800"
          >
            <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
          </button>
        </div>

        {/* Mobile panel */}
        {mobileOpen && (
          <div className="md:hidden pb-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitSearch(query);
              }}
              className="mb-3"
            >
              <div className="flex items-center gap-2 h-11 px-3 rounded-md border border-background-300 bg-background-100">
                <i className="ri-search-line text-foreground-500"></i>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索影视名称、类型"
                  aria-label="搜索影视"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                />
              </div>
            </form>
            <nav className="grid grid-cols-2 gap-2" aria-label="移动端导航">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-md text-sm text-center border transition-colors ${
                      isActive
                        ? 'border-primary-300 bg-primary-50 text-primary-700 font-semibold'
                        : 'border-background-200 text-foreground-700'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}